import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Upload, Loader2, Check, ImageIcon, AlertCircle } from 'lucide-react';
import { programsAPI, getImageUrl } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProgramImageCard = ({ program, onImageUpdated }) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const imgSrc = getImageUrl(program.image);
  const hasImage = program.image && program.image !== '';
  const isLocal = hasImage && program.image.startsWith('/api/uploads/');
  const isExternal = hasImage && (program.image.startsWith('http://') || program.image.startsWith('https://'));

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API_URL}/uploads/program-image`, formData);
      const imageUrl = uploadRes.data.primary_url;

      await programsAPI.update(program._id, { image: imageUrl });

      toast({
        title: language === 'es' ? 'Imagen actualizada' : 'Image updated',
        description: program.name,
      });
      onImageUpdated();
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'Upload failed',
        variant: 'destructive',
      });
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card
      className="border border-gray-200 hover:border-purple-300 transition-all overflow-hidden"
      data-testid={`image-card-${program._id}`}
    >
      <div
        className="relative h-44 bg-gray-100 cursor-pointer group"
        onClick={() => !uploading && fileRef.current?.click()}
      >
        {(preview || imgSrc) ? (
          <img
            src={preview || imgSrc}
            alt={program.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <ImageIcon className="w-12 h-12" />
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {uploading ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : (
            <div className="text-center text-white">
              <Upload className="w-8 h-8 mx-auto mb-1" />
              <span className="text-sm font-medium">
                {language === 'es' ? 'Cambiar imagen' : 'Change image'}
              </span>
            </div>
          )}
        </div>

        {/* Status badge */}
        <div className="absolute top-2 right-2">
          {!hasImage ? (
            <Badge className="bg-red-500 text-white text-xs">
              <AlertCircle className="w-3 h-3 mr-1" />
              {language === 'es' ? 'Sin imagen' : 'No image'}
            </Badge>
          ) : isLocal ? (
            <Badge className="bg-blue-500 text-white text-xs">
              <Check className="w-3 h-3 mr-1" /> R2
            </Badge>
          ) : isExternal ? (
            <Badge className="bg-gray-500 text-white text-xs">URL</Badge>
          ) : null}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
          data-testid={`upload-input-${program._id}`}
        />
      </div>

      <CardContent className="p-3">
        <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-0.5">
          {program.organization}
        </p>
        <h3 className="text-sm font-bold text-gray-900 line-clamp-1">{program.name}</h3>
        <div className="flex items-center gap-2 mt-1.5">
          <Badge
            variant="outline"
            className={`text-xs ${
              program.status === 'open'
                ? 'border-green-300 text-green-700'
                : program.status === 'draft'
                ? 'border-yellow-300 text-yellow-700'
                : 'border-gray-300 text-gray-500'
            }`}
          >
            {program.status}
          </Badge>
          {program.featured && (
            <Badge className="text-xs text-white" style={{ backgroundColor: '#ff9400' }}>
              {language === 'es' ? 'Destacado' : 'Featured'}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AdminImageManager = () => {
  const { language } = useLanguage();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | missing | r2 | external

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await programsAPI.getAll();
      setPrograms(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const filtered = programs.filter((p) => {
    if (filter === 'missing') return !p.image || p.image === '';
    if (filter === 'r2') return p.image?.startsWith('/api/uploads/');
    if (filter === 'external') return p.image?.startsWith('http');
    return true;
  });

  const stats = {
    total: programs.length,
    missing: programs.filter((p) => !p.image || p.image === '').length,
    r2: programs.filter((p) => p.image?.startsWith('/api/uploads/')).length,
    external: programs.filter((p) => p.image?.startsWith('http')).length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/dashboard"
              className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mb-2"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'es' ? 'Volver al Panel' : 'Back to Dashboard'}
            </Link>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">
              {language === 'es' ? 'Gestor de Imagenes' : 'Image Manager'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {language === 'es'
                ? 'Haz clic en cualquier imagen para cambiarla. Se sube automaticamente a Cloudflare R2.'
                : 'Click any image to replace it. Uploads automatically to Cloudflare R2.'}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`p-4 rounded-lg border text-left transition-all ${
              filter === 'all' ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            data-testid="filter-all"
          >
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">{language === 'es' ? 'Total desafíos' : 'Total challenges'}</p>
          </button>
          <button
            onClick={() => setFilter('missing')}
            className={`p-4 rounded-lg border text-left transition-all ${
              filter === 'missing' ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            data-testid="filter-missing"
          >
            <p className="text-2xl font-bold text-red-600">{stats.missing}</p>
            <p className="text-xs text-gray-500">{language === 'es' ? 'Sin imagen' : 'No image'}</p>
          </button>
          <button
            onClick={() => setFilter('r2')}
            className={`p-4 rounded-lg border text-left transition-all ${
              filter === 'r2' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            data-testid="filter-r2"
          >
            <p className="text-2xl font-bold text-blue-600">{stats.r2}</p>
            <p className="text-xs text-gray-500">Cloudflare R2</p>
          </button>
          <button
            onClick={() => setFilter('external')}
            className={`p-4 rounded-lg border text-left transition-all ${
              filter === 'external' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            data-testid="filter-external"
          >
            <p className="text-2xl font-bold text-gray-600">{stats.external}</p>
            <p className="text-xs text-gray-500">{language === 'es' ? 'URL externa' : 'External URL'}</p>
          </button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#6949b6' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {language === 'es' ? 'No hay desafíos en esta categoria' : 'No challenges in this category'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="image-grid">
            {filtered.map((program) => (
              <ProgramImageCard key={program._id} program={program} onImageUpdated={fetchPrograms} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImageManager;
